// AI Translate — a calm, logged-in translation trainer powered by Mistral.
//
// You pick an area (CEFR tier · theme · vocabulary-or-a-tense); the app asks the
// backend for ONE English sentence, you translate it to Spanish, and the tutor
// grades it (correct / could be said differently / wrong) with a short
// explanation and alternatives. No scores or streaks — the only number is a
// quiet running level estimate, kept on your profile.
//
// All AI calls go through pb_hooks/ai.pb.js so the Mistral key stays server-side;
// see src/lib/ai.js for the thin client.
import { VOCAB_THEMES, VOCAB_TIERS } from "../data/vocabulary.js";
import { TENSES, TENSE_LABELS } from "../data/index.js";
import { isAuthed } from "../lib/auth.js";
import { openAuthPanel } from "../ui/authPanel.js";
import { escapeHtml } from "../ui/helpers.js";
import {
  generateSentence,
  evaluateTranslation,
  fetchAiProfile,
  aiErrorMessage,
} from "../lib/ai.js";
import { addPersonalWord } from "../lib/personalVocab.js";

const THEME_LABEL = Object.fromEntries(VOCAB_THEMES.map((t) => [t.id, t.label]));

// Session state (mirrors the trainer.js module-global pattern).
let state = null;

// When the learner arrives from the Refine page, this holds the weak domain to
// steer generation toward. Cleared when they dismiss the banner.
let activeFocus = "";

const FOCUS_DOMAIN_LABEL = {
  sentence_structure: "Sentence structure",
  conjugation: "Conjugation",
  agreement: "Agreement",
  vocabulary: "Vocabulary",
  orthography: "Spelling & accents",
};

const VERDICT = {
  correct: { icon: "✓", label: "Spot on", box: "bg-sage-50 border-sage-200", text: "text-sage-700" },
  partial: { icon: "↺", label: "Almost — could be tidier", box: "bg-amber-50 border-amber-200", text: "text-amber-700" },
  incorrect: { icon: "✗", label: "Not quite", box: "bg-rose-50 border-rose-200", text: "text-rose-700" },
};

// Imperative & subjunctive are B1+ grammar, so they only appear once the chosen
// level is B or above. The value maps to a descriptive tense string for the tutor.
const FOCUS_EXTRA = {
  imperative: "imperative mood (commands)",
  subjunctive: "present subjunctive",
};

function buildFocusOptions(tier) {
  let html = `<option value="">Vocabulary (any tense)</option>`;
  html += TENSES.map((t) => `<option value="${t}">${escapeHtml(TENSE_LABELS[t])}</option>`).join("");
  const advanced = !!tier && (tier.charAt(0) === "B" || tier.charAt(0) === "C");
  if (advanced) {
    html += `<option value="imperative">Imperative (commands)</option>`;
    html += `<option value="subjunctive">Subjunctive</option>`;
  }
  return html;
}

export async function aiTrainerView(app, params = {}) {
  if (!isAuthed()) {
    renderGate(app);
    return;
  }

  // A focus domain passed from Refine ("Train this") steers generation.
  activeFocus = params.focus && FOCUS_DOMAIN_LABEL[params.focus] ? params.focus : "";

  const tierOpts = VOCAB_TIERS.map(
    (t) => `<option value="${t}"${t === "A2" ? " selected" : ""}>${t}</option>`
  ).join("");
  const themeOpts =
    `<option value="">Any theme</option>` +
    VOCAB_THEMES.map((t) => `<option value="${t.id}">${escapeHtml(t.label)}</option>`).join("");
  const focusOpts = buildFocusOptions("A2");

  app.innerHTML = `
    <section class="max-w-2xl mx-auto">
      <div class="flex items-start justify-between gap-3 mb-5">
        <div>
          <h2 class="text-2xl font-bold text-sage-800">AI Translate</h2>
          <p class="text-sage-600 text-sm mt-1">Translate one English sentence into Spanish. The tutor tells you if it's right, how else you could say it, or what to fix. No scores — just a quiet sense of your level.</p>
        </div>
        <span id="level-badge" class="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-sage-100 text-sage-700" title="Your rolling level estimate">Level …</span>
      </div>

      <div class="bg-white rounded-2xl border border-sage-100 p-4 mb-4 flex flex-wrap items-end gap-3">
        <label class="text-sm text-sage-700">Level
          <select id="ai-tier" class="mt-1 block border border-sage-200 rounded-lg px-2 py-1.5 text-sm bg-white">${tierOpts}</select>
        </label>
        <label class="text-sm text-sage-700">Theme
          <select id="ai-theme" class="mt-1 block border border-sage-200 rounded-lg px-2 py-1.5 text-sm bg-white">${themeOpts}</select>
        </label>
        <label class="text-sm text-sage-700">Focus
          <select id="ai-focus" class="mt-1 block border border-sage-200 rounded-lg px-2 py-1.5 text-sm bg-white">${focusOpts}</select>
        </label>
        <button id="ai-start" class="ml-auto bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">New sentence</button>
      </div>

      <div id="ai-focus-banner"></div>
      <div id="ai-stage"></div>
      <p class="text-[11px] text-sage-400 mt-4">Your translations are sent to Mistral AI for grading. Be mindful of sharing anything sensitive.</p>
    </section>`;

  app.querySelector("#ai-start").addEventListener("click", () => startExercise(app));
  renderFocusBanner(app);

  // Rebuild the Focus list when the level changes, so imperative/subjunctive
  // appear only at B-level and above.
  const tierSel = app.querySelector("#ai-tier");
  const focusSel = app.querySelector("#ai-focus");
  tierSel.addEventListener("change", () => {
    const cur = focusSel.value;
    focusSel.innerHTML = buildFocusOptions(tierSel.value);
    focusSel.value = [...focusSel.options].some((o) => o.value === cur) ? cur : "";
  });

  // Warm the level badge from the saved profile (updated again after each grade).
  fetchAiProfile()
    .then((p) => setLevel(app, p && p.ai_level))
    .catch(() => {});

  // Arriving with a weak-spot focus → jump straight into a targeted exercise.
  if (activeFocus) startExercise(app);

  window.scrollTo({ top: 0, behavior: "smooth" });
}

// The "training X" banner shown when a Refine focus is active. Dismissing it
// clears the focus so the next sentence is unsteered again.
function renderFocusBanner(app) {
  const slot = app.querySelector("#ai-focus-banner");
  if (!slot) return;
  if (!activeFocus) {
    slot.innerHTML = "";
    return;
  }
  slot.innerHTML = `
    <div class="flex items-center gap-2 bg-sage-50 border border-sage-200 rounded-xl px-3 py-2 mb-4 text-sm">
      <span aria-hidden="true">🎯</span>
      <span class="text-sage-700">Targeting your weak spot: <b>${escapeHtml(FOCUS_DOMAIN_LABEL[activeFocus] || activeFocus)}</b></span>
      <button id="ai-focus-clear" class="ml-auto text-sage-400 hover:text-sage-700 text-xs font-medium" aria-label="Clear focus">Clear ✕</button>
    </div>`;
  const clear = slot.querySelector("#ai-focus-clear");
  if (clear) clear.addEventListener("click", () => { activeFocus = ""; renderFocusBanner(app); });
}

function renderGate(app) {
  app.innerHTML = `
    <section class="max-w-xl mx-auto py-12 text-center">
      <p class="text-4xl mb-3">🤖</p>
      <h2 class="text-2xl font-bold text-sage-800">AI Translate</h2>
      <p class="text-sage-600 text-sm mt-2 mb-5">Practise translating English into Spanish with instant, friendly feedback from an AI tutor — and a quiet read on your level over time. Sign in to start.</p>
      <button id="ai-signin" class="bg-sage-500 hover:bg-sage-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium">Sign in</button>
    </section>`;
  app.querySelector("#ai-signin").addEventListener("click", () => openAuthPanel("signin"));
}

function setLevel(app, level) {
  const el = app.querySelector("#level-badge");
  if (el) el.textContent = level ? `Level ${level}` : "Level —";
}

function readArea(app) {
  const tier = app.querySelector("#ai-tier").value || "A2";
  const themeId = app.querySelector("#ai-theme").value || "";
  const focus = app.querySelector("#ai-focus").value || "";
  return {
    tier,
    themeId,
    theme: themeId ? THEME_LABEL[themeId] || themeId : "",
    tense: focus ? TENSE_LABELS[focus] || FOCUS_EXTRA[focus] || focus : "",
    focusDomain: activeFocus,
  };
}

async function startExercise(app) {
  const stage = app.querySelector("#ai-stage");
  const area = readArea(app);
  state = { area, sentence: "", vocab: [], grading: false };

  stage.innerHTML = thinkingCard("Crafting a sentence…");
  try {
    const res = await generateSentence(area);
    // New shape: { sentence, vocabulary }. Fall back to the old { english } so a
    // not-yet-updated backend still works.
    state.sentence = (res && (res.sentence || res.english)) || "";
    state.vocab = res && Array.isArray(res.vocabulary) ? res.vocabulary : [];
    if (!state.sentence) throw new Error("No sentence returned.");
    renderPrompt(app);
  } catch (err) {
    stage.innerHTML = errorCard(aiErrorMessage(err), "Try again");
    const retry = stage.querySelector("#ai-retry");
    if (retry) retry.addEventListener("click", () => startExercise(app));
  }
}

function renderPrompt(app) {
  const stage = app.querySelector("#ai-stage");
  const a = state.area;
  const scope = [a.tier, a.theme, a.tense].filter(Boolean).join(" · ");

  const hasRef = Array.isArray(state.vocab) && state.vocab.length > 0;

  stage.innerHTML = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 sm:p-6">
      <p class="text-[11px] uppercase tracking-wide text-sage-500 mb-3">${escapeHtml(scope)}</p>
      <p class="text-xs text-sage-400 uppercase tracking-wide">Translate into Spanish</p>
      <p class="text-xl font-semibold text-sage-800 mt-1 mb-4">${escapeHtml(state.sentence)}</p>
      <form id="ai-form" class="flex items-start gap-2">
        <textarea id="ai-answer" rows="1" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false"
          class="flex-1 px-4 py-3 border border-sage-200 rounded-xl focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none text-lg resize-none overflow-hidden leading-snug"
          placeholder="Escribe tu traducción…"></textarea>
        ${hasRef ? `<button type="button" id="ai-ref-toggle" title="Reference" aria-label="Show reference words" aria-expanded="false" class="shrink-0 px-3 py-3 rounded-xl border border-sage-200 text-sage-600 hover:border-sage-300 transition text-lg leading-none">📖</button>` : ""}
        <button type="submit" class="shrink-0 bg-sage-500 hover:bg-sage-600 text-white px-5 py-3 rounded-xl font-medium">Check</button>
      </form>
      ${hasRef ? `<div id="ai-ref" class="hidden mt-3"></div>` : ""}
      <div id="ai-feedback" class="mt-4"></div>
    </div>`;

  const input = stage.querySelector("#ai-answer");
  input.focus();
  // Grow the box with the answer so long translations stay fully visible.
  const autoGrow = () => {
    input.style.height = "auto";
    input.style.height = input.scrollHeight + "px";
  };
  input.addEventListener("input", autoGrow);
  autoGrow();
  // Enter submits; Shift+Enter starts a new line.
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitAnswer(app, input.value);
    }
  });
  stage.querySelector("#ai-form").addEventListener("submit", (e) => {
    e.preventDefault();
    submitAnswer(app, input.value);
  });

  if (hasRef) {
    const refArea = stage.querySelector("#ai-ref");
    const refToggle = stage.querySelector("#ai-ref-toggle");
    refArea.innerHTML = refCardsHtml(state.vocab);
    refToggle.addEventListener("click", () => {
      const open = refArea.classList.toggle("hidden") === false;
      refToggle.setAttribute("aria-expanded", open ? "true" : "false");
      refToggle.classList.toggle("bg-sage-50", open);
      refToggle.classList.toggle("border-sage-300", open);
    });
    refArea.querySelectorAll("[data-ref-card]").forEach((card) => {
      card.addEventListener("click", () => {
        const back = card.querySelector(".ref-back");
        if (back.dataset.revealed === "1") {
          back.textContent = "tap to reveal";
          back.className = "block text-[11px] text-sage-400 ref-back";
          back.dataset.revealed = "0";
        } else {
          const es = back.dataset.es || "";
          const pos = back.dataset.pos || "";
          back.textContent = es + (pos ? " · " + pos : "");
          back.className = "block text-[11px] text-sage-700 ref-back";
          back.dataset.revealed = "1";
          // Revealing a card = "I didn't remember this" → save it to the user's
          // personal flashcard deck (de-duped server-side). Quiet ✓ on success.
          addPersonalWord({
            word: back.dataset.word,
            es: es,
            pos: pos,
            sentence: state.sentence,
            tier: state.area.tier,
            theme: state.area.theme,
          }).then((r) => {
            if (r && r.added && back.dataset.revealed === "1") {
              back.textContent = back.textContent + "  ✓";
            }
          });
        }
      });
    });
  }
}

// Reference cards: each shows the English word from the sentence; tapping reveals
// the Spanish dictionary form (infinitive / noun+article) — a reference, not the
// finished answer (the learner still conjugates and agrees it).
function refCardsHtml(vocab) {
  return `
    <div class="flex flex-wrap gap-2">
      ${vocab
        .map((v) => {
          const word = escapeHtml(String(v.word || ""));
          const es = escapeHtml(String(v.es || ""));
          const pos = escapeHtml(String(v.pos || ""));
          return `
            <button type="button" data-ref-card class="ref-card text-left px-3 py-2 rounded-lg border border-sage-200 bg-sage-50/40 hover:border-sage-300 transition">
              <span class="block text-sm font-medium text-sage-800">${word}</span>
              <span class="block text-[11px] text-sage-400 ref-back" data-word="${word}" data-es="${es}" data-pos="${pos}" data-revealed="0">tap to reveal</span>
            </button>`;
        })
        .join("")}
    </div>
    <p class="text-[11px] text-sage-400 mt-2">Words you reveal are saved to your flashcards (“My words”).</p>`;
}

async function submitAnswer(app, raw) {
  const answer = (raw || "").trim();
  if (!answer || state.grading) return;
  state.grading = true;

  const input = app.querySelector("#ai-answer");
  const form = app.querySelector("#ai-form");
  const fb = app.querySelector("#ai-feedback");
  if (input) input.disabled = true;
  if (form) form.querySelector("button").disabled = true;
  fb.innerHTML = `<p class="text-sm text-sage-500">Checking…</p>`;

  try {
    const res = await evaluateTranslation({
      english: state.sentence,
      answer,
      tier: state.area.tier,
      theme: state.area.theme,
      tense: state.area.tense,
    });
    renderFeedback(app, answer, res);
    setLevel(app, res && res.level);
  } catch (err) {
    fb.innerHTML = errorCard(aiErrorMessage(err), "Try again");
    const retry = fb.querySelector("#ai-retry");
    if (retry) retry.addEventListener("click", () => submitAnswer(app, answer));
  } finally {
    state.grading = false;
  }
}

function renderFeedback(app, answer, res) {
  const fb = app.querySelector("#ai-feedback");
  const v = VERDICT[res.verdict] || VERDICT.partial;
  const alts = Array.isArray(res.alternatives) ? res.alternatives.filter(Boolean) : [];
  // Words the grader flagged as worth drilling — one tap saves them to the deck.
  const review = Array.isArray(res.review_words)
    ? res.review_words.filter((w) => w && (w.es || w.word)).slice(0, 3)
    : [];

  fb.innerHTML = `
    <div class="rounded-xl border p-4 ${v.box}">
      <p class="font-bold ${v.text} mb-2">${v.icon} ${escapeHtml(v.label)}</p>
      <p class="text-sm text-sage-600 mb-2">You wrote: <span class="text-sage-800">${escapeHtml(answer)}</span></p>
      ${res.corrected ? `<p class="text-sm text-sage-700">Natural Spanish: <span class="font-semibold bg-white/70 rounded px-1">${escapeHtml(res.corrected)}</span></p>` : ""}
      ${res.explanation ? `<p class="text-sm text-sage-600 mt-2">${escapeHtml(res.explanation)}</p>` : ""}
      ${
        alts.length
          ? `<div class="mt-3"><p class="text-xs text-sage-500 uppercase tracking-wide mb-1">Other ways to say it</p>
             <ul class="list-disc pl-5 text-sm text-sage-700 space-y-0.5">${alts.map((x) => `<li>${escapeHtml(x)}</li>`).join("")}</ul></div>`
          : ""
      }
      ${
        review.length
          ? `<div class="mt-3 pt-3 border-t border-white/60">
               <p class="text-xs text-sage-500 uppercase tracking-wide mb-1">Worth adding to My words</p>
               <div class="flex flex-wrap gap-2">
                 ${review
                   .map(
                     (w, i) => `<button type="button" data-add-word="${i}" class="text-left px-3 py-1.5 rounded-lg border border-sage-200 bg-white hover:border-sage-300 transition text-sm">
                       <span class="font-medium text-sage-800">${escapeHtml(w.es || "")}</span>${w.word ? `<span class="text-sage-400 text-xs"> · ${escapeHtml(w.word)}</span>` : ""}
                       <span class="add-state text-xs text-sage-400 ml-1">+ add</span>
                     </button>`
                   )
                   .join("")}
               </div>
             </div>`
          : ""
      }
      <button id="ai-next" class="mt-4 bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Next sentence →</button>
    </div>`;

  fb.querySelectorAll("[data-add-word]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const w = review[Number(btn.dataset.addWord)];
      if (!w) return;
      const stateEl = btn.querySelector(".add-state");
      btn.disabled = true;
      if (stateEl) stateEl.textContent = "…";
      addPersonalWord({
        word: w.word,
        es: w.es,
        pos: w.pos,
        sentence: state.sentence,
        tier: state.area.tier,
        theme: state.area.theme,
      }).then((r) => {
        if (stateEl) stateEl.textContent = r && r.reason === "exists" ? "✓ in deck" : "✓ saved";
        if (stateEl) stateEl.className = "add-state text-xs text-sage-600 ml-1";
      });
    });
  });

  const next = fb.querySelector("#ai-next");
  next.addEventListener("click", () => startExercise(app));
  next.focus();
}

function thinkingCard(msg) {
  return `
    <div class="bg-white rounded-2xl border border-sage-100 p-6 text-center">
      <p class="text-sage-500 text-sm">${escapeHtml(msg)}</p>
    </div>`;
}

function errorCard(msg, btnLabel) {
  return `
    <div class="bg-white rounded-2xl border border-rose-100 p-5">
      <p class="text-sm text-rose-600 mb-3">${escapeHtml(msg)}</p>
      <button id="ai-retry" class="bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">${escapeHtml(btnLabel)}</button>
    </div>`;
}
